'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { Clock, CheckCircle2, XCircle, Play, Music } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Request {
  id: string
  songName: string
  artist?: string | null
  status: string
}

export default function RequestList({ requests }: { requests: Request[] }) {
  if (requests.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-6">
          <Music className="w-10 h-10 text-zinc-700" />
        </div>
        <p className="text-zinc-500 text-lg font-medium mb-1">Queue is Empty</p>
        <p className="text-zinc-700 text-sm">Be the first to drop a track!</p>
      </div>
    )
  }

  const statusConfig: Record<string, { icon: any; color: string; bg: string }> = {
    PENDING: { icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10 border-amber-500/20" },
    ACCEPTED: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-500/10 border-emerald-500/20" },
    PLAYED: { icon: Play, color: "text-neon-pink", bg: "bg-neon-pink/10 border-neon-pink/20" },
    REJECTED: { icon: XCircle, color: "text-red-500", bg: "bg-red-500/10 border-red-500/20" }
  }

  return (
    <ul className="space-y-3">
      <AnimatePresence initial={false}>
        {requests.map((req, i) => {
          const config = statusConfig[req.status] || statusConfig.PENDING
          const Icon = config.icon

          return (
            <motion.li
              key={req.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
            >
              <div className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.05] hover:border-white/10 transition-all group">
                {/* Track Number */}
                <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center text-zinc-600 font-bold text-sm group-hover:text-white transition-colors">
                  {String(i + 1).padStart(2, '0')}
                </div>
                
                {/* Song Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-white truncate group-hover:text-neon-pink transition-colors">
                    {req.songName}
                  </h4>
                  <p className="text-sm text-zinc-600 truncate">{req.artist || 'Unknown Artist'}</p>
                </div>

                {/* Status Badge */}
                <div className={cn("px-3 py-1.5 rounded-lg border flex items-center gap-2", config.bg)}>
                  <Icon className={cn("w-4 h-4", config.color)} />
                  <span className={cn("text-xs font-bold uppercase", config.color)}>{req.status}</span>
                </div>
              </div>
            </motion.li>
          )
        })}
      </AnimatePresence>
    </ul>
  )
}
